import moment from 'moment-timezone';
import firebase from '../config/firebase.config';
import { getTeamMemberDetails } from './teamMemberDetails';
import memberSessionService from './memberSessions';

const db = firebase.firestore();

const DEFAULT_TZ = 'Asia/Kolkata';
const LOG_PREFIX = '[TeamsTimeline]';

/** Normalize Firestore Timestamp or ISO string to a value moment() can parse */
function toMomentInput(raw) {
  if (raw == null) return null;
  if (typeof raw?.toDate === 'function') return raw.toDate();
  return raw;
}

/**
 * Aggregate one member's sessions for a single day in their timezone.
 * Handles sessions that cross midnight by clamping to day boundaries and
 * proportionally allocating idle time.
 */
function aggregateSessionsForDay(sessions, date, timeZone) {
  const tz = timeZone || DEFAULT_TZ;
  const dayStart = moment.tz(date, 'YYYY-MM-DD', tz).startOf('day');
  const dayEnd = dayStart.clone().endOf('day');

  let workSec = 0;
  let idleSec = 0;
  let firstStart = null;
  let lastEnd = null;

  (sessions || []).forEach((session) => {
    const startRaw = session.startTime;
    const endRaw =
      session.endTime ||
      session.lastPingAt ||
      (startRaw && session.duration ? null : null);
    if (!startRaw) return;

    const startInput = toMomentInput(startRaw);
    const endInput = toMomentInput(endRaw);
    const startMoment = moment(startInput).tz(tz);
    let endMoment = endInput
      ? moment(endInput).tz(tz)
      : startMoment.clone().add(session.duration || 0, 'seconds');
    if (!endMoment.isValid()) {
      endMoment = startMoment.clone().add(session.duration || 0, 'seconds');
    }

    const segmentStart = moment.max(startMoment, dayStart);
    const segmentEnd = moment.min(endMoment, dayEnd);
    if (segmentStart.isBefore(segmentEnd) || segmentStart.isSame(segmentEnd)) {
      const segmentDurationSec = Math.max(
        0,
        segmentEnd.diff(segmentStart, 'seconds')
      );
      workSec += segmentDurationSec;

      const totalDuration = session.duration || 0;
      const totalIdle = session.idleDuration || 0;
      if (totalDuration > 0) {
        idleSec += (segmentDurationSec / totalDuration) * totalIdle;
      }

      const segStartDate = segmentStart.toDate();
      const segEndDate = segmentEnd.toDate();
      if (!firstStart || segStartDate < firstStart) firstStart = segStartDate;
      if (!lastEnd || segEndDate > lastEnd) lastEnd = segEndDate;
    }
  });

  const productiveSec = Math.max(0, workSec - idleSec);
  return {
    workSec,
    idleSec,
    productiveSec,
    firstStart,
    lastEnd,
  };
}

function logAggregate(userUID, date, tz, sessionCount, aggregated) {
  if (sessionCount === 0) return;
  console.log(`${LOG_PREFIX} aggregateSessionsForDay`, {
    userUID,
    date,
    timeZone: tz,
    sessionCount,
    workSec: aggregated.workSec,
    idleSec: aggregated.idleSec,
    productiveSec: aggregated.productiveSec,
    checkIn: aggregated.firstStart
      ? moment(aggregated.firstStart).tz(tz).format('YYYY-MM-DD HH:mm')
      : null,
    checkOut: aggregated.lastEnd
      ? moment(aggregated.lastEnd).tz(tz).format('YYYY-MM-DD HH:mm')
      : null,
  });
}

/**
 * Fetch team timeline for a single date: all members with their work/productive/idle
 * and check-in/check-out for that day. Fetches sessions for all members in parallel.
 *
 * @param {{ teamId: string, date: string (YYYY-MM-DD), companyId?: string }} params
 * @returns {Promise<{ success: boolean, data?: Array<{ userUID, userName, timeZone, checkIn, checkOut, workSec, idleSec, productiveSec }>, error?: any }>}
 */
export async function getTeamTimelineForDate({ teamId, date, companyId }) {
  console.log(`${LOG_PREFIX} getTeamTimelineForDate called`, {
    teamId,
    date,
    companyId: companyId || null,
  });

  if (!teamId || !date) {
    console.warn(
      `${LOG_PREFIX} getTeamTimelineForDate validation failed: missing teamId or date`
    );
    return { success: false, error: 'teamId and date are required' };
  }

  try {
    console.log(`${LOG_PREFIX} fetching team members`, { teamId });
    const memberDetails = await getTeamMemberDetails(db, teamId);
    const members = Object.entries(memberDetails || {})
      .map(([uid, m]) =>
        m && typeof m === 'object' ? { userUID: uid, ...m } : null
      )
      .filter((m) => m && m.userUID && !m.deleted);
    console.log(`${LOG_PREFIX} members after filter`, {
      total: members.length,
      userUIDs: members.map((m) => m.userUID),
    });
    if (members.length === 0) {
      console.log(`${LOG_PREFIX} no members to fetch, returning empty data`);
      return { success: true, data: [] };
    }

    console.log(
      `${LOG_PREFIX} starting parallel session fetch for ${members.length} member(s)`
    );
    const sessionPromises = members.map((member) =>
      memberSessionService.getSessionsForRange({
        teamId,
        userUID: member.userUID,
        startDate: date,
        endDate: date,
        timeZone: member.timeZone || DEFAULT_TZ,
        companyId: companyId || undefined,
        employeeId: member.employeeId || undefined,
      })
    );

    const results = await Promise.allSettled(sessionPromises);
    const fulfilled = results.filter((r) => r.status === 'fulfilled').length;
    const rejected = results.filter((r) => r.status === 'rejected').length;
    console.log(`${LOG_PREFIX} session fetch completed`, {
      total: results.length,
      fulfilled,
      rejected,
    });

    const rows = results.map((result, index) => {
      const member = members[index];
      const userName =
        member.userName ||
        member.userEmailid ||
        member.label ||
        member.userUID ||
        '—';
      const timeZone = member.timeZone || DEFAULT_TZ;

      if (result.status === 'rejected') {
        console.warn(`${LOG_PREFIX} session fetch rejected for member`, {
          userUID: member.userUID,
          userName,
          reason: result.reason?.message || result.reason,
        });
        return {
          userUID: member.userUID,
          userName,
          timeZone,
          checkIn: null,
          checkOut: null,
          lastActivityAt: null,
          workSec: 0,
          idleSec: 0,
          productiveSec: 0,
          error: result.reason?.message || 'Failed to load sessions',
        };
      }

      const response = result.value;
      if (!response?.success || !response?.data?.sessions) {
        console.log(`${LOG_PREFIX} no sessions for member`, {
          userUID: member.userUID,
          userName,
          success: response?.success,
          sessionCount: response?.data?.sessions?.length ?? 0,
        });
        return {
          userUID: member.userUID,
          userName,
          timeZone,
          checkIn: null,
          checkOut: null,
          lastActivityAt: null,
          workSec: 0,
          idleSec: 0,
          productiveSec: 0,
        };
      }

      const sessions = response.data.sessions;
      const aggregated = aggregateSessionsForDay(sessions, date, timeZone);
      logAggregate(member.userUID, date, timeZone, sessions.length, aggregated);

      return {
        userUID: member.userUID,
        userName,
        timeZone,
        checkIn: aggregated.firstStart,
        checkOut: aggregated.lastEnd,
        lastActivityAt: aggregated.lastEnd,
        workSec: aggregated.workSec,
        idleSec: aggregated.idleSec,
        productiveSec: aggregated.productiveSec,
      };
    });

    console.log(`${LOG_PREFIX} getTeamTimelineForDate success`, {
      date,
      rowCount: rows.length,
      withSessions: rows.filter((r) => r.workSec > 0).length,
    });
    return { success: true, data: rows };
  } catch (err) {
    console.error(`${LOG_PREFIX} getTeamTimelineForDate error`, err);
    return {
      success: false,
      error: err?.message || 'Failed to load department timeline',
    };
  }
}

export default {
  getTeamTimelineForDate,
};

/**
 * Fetch last activity heartbeat for all members in a team.
 * Source priority: presence.lastPingAt -> lastPingAt (legacy field)
 */
export async function getTeamMemberLastActivityMap(teamId) {
  if (!teamId) return {};
  const map = {};
  const membersSnap = await db
    .collection('teamDatabase')
    .doc(teamId)
    .collection('members')
    .get();

  membersSnap.forEach((docSnap) => {
    const member = docSnap.data() || {};
    if (member.deleted) return;
    map[docSnap.id] =
      member?.presence?.lastPingAt || member?.lastPingAt || null;
  });
  return map;
}
